import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Button,
  Group,
  Switch,
  Divider,
  Grid,
  Card,
  ThemeIcon,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { 
  IconBrandFacebook, 
  IconBrandInstagram, 
  IconBrandLinkedin, 
  IconBrandTwitter,
  IconBrandYoutube,
  IconBrandPinterest,
  IconDeviceFloppy,
} from '@tabler/icons-react';

interface SocialMediaSettings {
  facebook: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  instagram: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  linkedin: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  twitter: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  youtube: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  pinterest: {
    url: string;
    enabled: boolean;
    showFeed: boolean;
  };
  openGraphEnabled: boolean;
  twitterCardsEnabled: boolean;
}

export const SocialMediaPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  const form = useForm<SocialMediaSettings>({
    initialValues: {
      facebook: { url: '', enabled: false, showFeed: false },
      instagram: { url: '', enabled: false, showFeed: false },
      linkedin: { url: '', enabled: false, showFeed: false },
      twitter: { url: '', enabled: false, showFeed: false },
      youtube: { url: '', enabled: false, showFeed: false },
      pinterest: { url: '', enabled: false, showFeed: false },
      openGraphEnabled: true,
      twitterCardsEnabled: true,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/social`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      form.setValues(response.data);
    } catch (error) {
      console.error('Error fetching social media settings:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load social media settings',
        color: 'red',
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: SocialMediaSettings) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/settings/social`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      notifications.show({
        title: 'Success',
        message: 'Social media settings updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update social media settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  const socialPlatforms = [
    { key: 'facebook', icon: IconBrandFacebook, color: '#1877F2', label: 'Facebook' },
    { key: 'instagram', icon: IconBrandInstagram, color: '#E4405F', label: 'Instagram' },
    { key: 'linkedin', icon: IconBrandLinkedin, color: '#0A66C2', label: 'LinkedIn' },
    { key: 'twitter', icon: IconBrandTwitter, color: '#1DA1F2', label: 'Twitter' },
    { key: 'youtube', icon: IconBrandYoutube, color: '#FF0000', label: 'YouTube' },
    { key: 'pinterest', icon: IconBrandPinterest, color: '#BD081C', label: 'Pinterest' },
  ];

  return (
    <Container size="lg">
      <Title order={2} mb="lg">Social Media Settings</Title>

      <Paper withBorder p="xl" radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Title order={4} mb="md">Social Media Profiles</Title>
            
            <Grid>
              {socialPlatforms.map((platform) => (
                <Grid.Col key={platform.key} span={{ base: 12, md: 6 }}>
                  <Card withBorder padding="md" radius="md">
                    <Group mb="md">
                      <ThemeIcon size="lg" radius="md" style={{ backgroundColor: platform.color }}>
                        <platform.icon size={20} />
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          label={platform.label}
                          placeholder={`https://${platform.key}.com/...`}
                          {...form.getInputProps(`${platform.key}.url`)}
                        />
                      </div>
                    </Group>
                    
                    <Group justify="space-between" mt="xs">
                      <Switch
                        label="Enable"
                        {...form.getInputProps(`${platform.key}.enabled`, { type: 'checkbox' })}
                      />
                      <Switch
                        label="Show Feed"
                        {...form.getInputProps(`${platform.key}.showFeed`, { type: 'checkbox' })}
                        disabled={!((form.values[platform.key as keyof SocialMediaSettings] as { enabled: boolean })?.enabled)}
                      />
                    </Group>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>

            <Divider my="md" label="SEO Integration" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="Open Graph Tags"
                  description="Enable Open Graph meta tags for better social sharing"
                  {...form.getInputProps('openGraphEnabled', { type: 'checkbox' })}
                  size="md"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="Twitter Cards"
                  description="Enable Twitter Cards for better tweet integration"
                  {...form.getInputProps('twitterCardsEnabled', { type: 'checkbox' })}
                  size="md"
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="xl">
              <Button 
                type="submit" 
                color="exportGreen" 
                loading={loading}
                leftSection={<IconDeviceFloppy size={16} />}
                size="md"
              >
                Save Settings
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};